import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Search, Phone, IdCard, Pencil, Trash2, MapPin, Navigation, Ban, FileText, UserCheck, Clock, History, ArrowRightLeft, Eye, X, Check } from "lucide-react";
import NivelBadge from "../components/NivelBadge";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ClientesPage() {
  const [clientes, setClientes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtroNivel, setFiltroNivel] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const [carros, setCarros] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [savingLocation, setSavingLocation] = useState(false);
  // Cadastros Pendentes (vindos da loja virtual pública)
  const [pendentes, setPendentes] = useState([]);
  const [showPendentes, setShowPendentes] = useState(false);
  const [aprovarOpen, setAprovarOpen] = useState(false);
  const [clienteAprovando, setClienteAprovando] = useState(null);
  const [aprovarForm, setAprovarForm] = useState({
    carro_id: "",
    rota_id: "",
    tipo_cliente: "cliente_final",
    limite_compra: ""
  });
  const [aprovarRotas, setAprovarRotas] = useState([]);
  const [aprovando, setAprovando] = useState(false);
  // Histórico de compras (modal)
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [notaVisualizando, setNotaVisualizando] = useState(null); // promissoria completa
  // Migração em massa
  const [migrarOpen, setMigrarOpen] = useState(false);
  const [migrarSelecionados, setMigrarSelecionados] = useState(new Set());
  const [migrarBusca, setMigrarBusca] = useState("");
  const [migrarFiltroCidade, setMigrarFiltroCidade] = useState("");
  const [migrarFiltroRota, setMigrarFiltroRota] = useState("");
  const [migrarCarroDest, setMigrarCarroDest] = useState("");
  const [migrarRotaDest, setMigrarRotaDest] = useState("");
  const [migrarRotasList, setMigrarRotasList] = useState([]);
  const [migrarTodasRotas, setMigrarTodasRotas] = useState([]); // rotas de TODOS os carros (para filtro)
  const [migrando, setMigrando] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.tipo === 'admin' || user.tipo === 'secretario';
  const canDelete = user.tipo === 'admin' || user.permissoes?.excluir_cliente;
  const canEdit = user.tipo === 'admin' || user.permissoes?.editar_cliente;
  const [formData, setFormData] = useState({
    nome_completo: "",
    telefone: "",
    cpf_rg: "",
    endereco: "",
    bairro: "",
    endereco_secundario: "",
    cidade_estado: "",
    data_nascimento: "",
    carro_id: "",
    rota_id: "",
    localizacao: null,
    bloqueado: false,
    limite_compra: "",
    nivel: "bronze",
    tipo_cliente: "salao_revenda"
  });

  useEffect(() => {
    loadClientes();
    loadCarros();
    if (isAdmin) loadPendentes();
  }, []);

  useEffect(() => {
    if (formData.carro_id) {
      loadRotas(formData.carro_id);
    }
  }, [formData.carro_id]);

  // Carrega rotas do carro escolhido no modal de aprovação
  useEffect(() => {
    if (aprovarForm.carro_id) {
      loadRotasAprovacao(aprovarForm.carro_id);
    } else {
      setAprovarRotas([]);
    }
  }, [aprovarForm.carro_id]);

  const loadClientes = async () => {
    try {
      const response = await axios.get(`${API}/clientes`);
      setClientes(response.data);
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    }
  };

  const loadCarros = async () => {
    try {
      const response = await axios.get(`${API}/carros`);
      setCarros(response.data);
    } catch (error) {
      console.error("Erro ao carregar carros:", error);
    }
  };

  // === Histórico de compras do cliente
  const abrirHistorico = async (cliente) => {
    setHistoricoOpen(true);
    setHistoricoLoading(true);
    setHistoricoData(null);
    try {
      const { data } = await axios.get(`${API}/clientes/${cliente.id}/historico`);
      setHistoricoData(data);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
      setHistoricoOpen(false);
    } finally {
      setHistoricoLoading(false);
    }
  };

  const abrirNotaCompleta = async (promissoria_id) => {
    try {
      const { data } = await axios.get(`${API}/promissorias/${promissoria_id}`);
      setNotaVisualizando(data);
    } catch (error) {
      toast.error("Erro ao carregar nota");
    }
  };

  // === Migração em massa
  const abrirMigrar = async () => {
    setMigrarSelecionados(new Set());
    setMigrarBusca("");
    setMigrarFiltroCidade("");
    setMigrarFiltroRota("");
    setMigrarCarroDest("");
    setMigrarRotaDest("");
    setMigrarRotasList([]);
    // Carrega rotas de TODOS os carros para popular o filtro por rota
    try {
      const promises = (carros || []).map((c) => axios.get(`${API}/rotas`, { params: { carro_id: c.id } }).then((r) => r.data || []).catch(() => []));
      const arrays = await Promise.all(promises);
      const flat = arrays.flat().filter((r) => r.tipo === "novas_vendas");
      // Remove duplicatas de nome (mesma rota espelhada em vários carros vira uma opção)
      setMigrarTodasRotas(flat);
    } catch {
      setMigrarTodasRotas([]);
    }
    setMigrarOpen(true);
  };

  const carregarRotasDest = async (carro_id) => {
    if (!carro_id) { setMigrarRotasList([]); return; }
    try {
      const { data } = await axios.get(`${API}/rotas`, { params: { carro_id } });
      setMigrarRotasList(Array.isArray(data) ? data : []);
    } catch {
      setMigrarRotasList([]);
    }
  };

  const toggleSelMigrar = (id) => {
    setMigrarSelecionados((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selecionarTodosMigrar = () => {
    // Seleciona só os clientes visíveis no filtro atual
    const alvo = clientesFiltradosParaMigrar();
    const todos = new Set(alvo.map((c) => c.id));
    if (todos.size === migrarSelecionados.size) {
      // Se todos já estão selecionados → desmarca
      setMigrarSelecionados(new Set());
    } else {
      setMigrarSelecionados(todos);
    }
  };

  const clientesFiltradosParaMigrar = () => {
    const q = (migrarBusca || "").toLowerCase().trim();
    const cid = (migrarFiltroCidade || "").toLowerCase().trim();
    const rotaId = migrarFiltroRota || "";
    return clientes.filter((c) => {
      // Busca textual (nome/telefone/CPF)
      if (q) {
        const okQ = (
          (c.nome_completo || "").toLowerCase().includes(q) ||
          (c.telefone || "").toLowerCase().includes(q) ||
          (c.cpf_rg || "").toLowerCase().includes(q)
        );
        if (!okQ) return false;
      }
      // Cidade (substring)
      if (cid && !((c.cidade_estado || "").toLowerCase().includes(cid))) {
        return false;
      }
      // Rota (rota_id do cliente é string separada por vírgula)
      if (rotaId) {
        const rotas_do_cli = (c.rota_id || "").split(",").map((s) => s.trim());
        if (!rotas_do_cli.includes(rotaId)) return false;
      }
      return true;
    });
  };

  // Opções únicas de cidade e rota para os filtros
  const cidadesUnicas = () => {
    const s = new Set();
    for (const c of clientes) {
      const v = (c.cidade_estado || "").trim();
      if (v) s.add(v);
    }
    return Array.from(s).sort();
  };

  const rotasUsadas = () => {
    // Lista todas as rotas usadas por algum cliente (id → nome). Usa `rotas` já carregadas
    // — mas rotas é do carro selecionado; para migrar precisamos das rotas de TODOS os carros.
    // Vamos usar migrarTodasRotas (state alimentado no abrirMigrar).
    return migrarTodasRotas;
  };

  const confirmarMigracao = async () => {
    if (migrarSelecionados.size === 0) {
      toast.error("Selecione pelo menos 1 cliente");
      return;
    }
    if (!migrarCarroDest && !migrarRotaDest) {
      toast.error("Escolha o carro e/ou a rota de destino");
      return;
    }
    setMigrando(true);
    try {
      const { data } = await axios.post(`${API}/clientes/migrar`, {
        cliente_ids: Array.from(migrarSelecionados),
        carro_id_destino: migrarCarroDest || null,
        rota_id_destino: migrarRotaDest || null,
      });
      toast.success(`${data.clientes_atualizados} cliente(s) migrado(s), ${data.notas_atualizadas} nota(s) atualizada(s)`);
      setMigrarOpen(false);
      loadClientes();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao migrar clientes");
    } finally {
      setMigrando(false);
    }
  };

  const loadRotas = async (carroId) => {
    try {
      const [retorno, novasVendas] = await Promise.all([
        axios.get(`${API}/rotas?carro_id=${carroId}&tipo=retorno`),
        axios.get(`${API}/rotas?carro_id=${carroId}&tipo=novas_vendas`)
      ]);
      
      const todasRotas = [...retorno.data, ...novasVendas.data];
      
      const rotasAgrupadas = {};
      todasRotas.forEach(rota => {
        const nomeNormalizado = rota.nome.trim().toUpperCase();
        if (!rotasAgrupadas[nomeNormalizado]) {
          rotasAgrupadas[nomeNormalizado] = {
            nomeOriginal: rota.nome.trim(),
            rotas: []
          };
        }
        rotasAgrupadas[nomeNormalizado].rotas.push(rota);
      });
      
      const rotasUnicas = Object.values(rotasAgrupadas).map(grupo => ({
        nome: grupo.nomeOriginal,
        id: grupo.rotas.map(r => r.id).join(','),
        ids_array: grupo.rotas.map(r => r.id),
        quantidade: grupo.rotas.length
      }));
      
      setRotas(rotasUnicas);
    } catch (error) {
      console.error("Erro ao carregar rotas:", error);
      setRotas([]);
    }
  };

  // ====== Cadastros pendentes (loja pública) ======
  const loadPendentes = async () => {
    try {
      const { data } = await axios.get(`${API}/clientes/pendentes`);
      setPendentes(data || []);
    } catch (err) {
      // silencioso
    }
  };

  const loadRotasAprovacao = async (carroId) => {
    try {
      const [retorno, novasVendas] = await Promise.all([
        axios.get(`${API}/rotas?carro_id=${carroId}&tipo=retorno`),
        axios.get(`${API}/rotas?carro_id=${carroId}&tipo=novas_vendas`)
      ]);
      const todasRotas = [...retorno.data, ...novasVendas.data];
      const rotasAgrupadas = {};
      todasRotas.forEach(rota => {
        const nomeNormalizado = rota.nome.trim().toUpperCase();
        if (!rotasAgrupadas[nomeNormalizado]) {
          rotasAgrupadas[nomeNormalizado] = { nomeOriginal: rota.nome.trim(), rotas: [] };
        }
        rotasAgrupadas[nomeNormalizado].rotas.push(rota);
      });
      const rotasUnicas = Object.values(rotasAgrupadas).map(grupo => ({
        nome: grupo.nomeOriginal,
        id: grupo.rotas.map(r => r.id).join(','),
      }));
      setAprovarRotas(rotasUnicas);
    } catch (err) {
      setAprovarRotas([]);
    }
  };

  const openAprovar = (cliente) => {
    setClienteAprovando(cliente);
    setAprovarForm({
      carro_id: "",
      rota_id: "",
      tipo_cliente: "cliente_final",
      limite_compra: ""
    });
    setAprovarRotas([]);
    setAprovarOpen(true);
  };

  const submitAprovar = async (e) => {
    e?.preventDefault?.();
    if (!aprovarForm.carro_id || !aprovarForm.rota_id) {
      toast.error("Selecione carro e rota");
      return;
    }
    setAprovando(true);
    try {
      await axios.post(`${API}/clientes/${clienteAprovando.id}/aprovar-cadastro`, {
        carro_id: aprovarForm.carro_id,
        rota_id: aprovarForm.rota_id,
        tipo_cliente: aprovarForm.tipo_cliente,
        limite_compra: aprovarForm.limite_compra ? parseFloat(aprovarForm.limite_compra) : 0,
      });
      toast.success(`${clienteAprovando.nome_completo} aprovado com sucesso!`);
      setAprovarOpen(false);
      setClienteAprovando(null);
      loadPendentes();
      loadClientes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aprovar cadastro");
    } finally {
      setAprovando(false);
    }
  };

  const rejeitarPendente = async (cliente) => {
    if (!window.confirm(`Rejeitar e excluir o cadastro pendente de "${cliente.nome_completo}"?`)) return;
    try {
      await axios.delete(`${API}/clientes/${cliente.id}`);
      toast.success("Cadastro pendente excluído");
      loadPendentes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleSaveLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocalização não suportada neste navegador");
      return;
    }
    setSavingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setFormData(prev => ({ ...prev, localizacao: loc }));
        setSavingLocation(false);
        toast.success(`Localização salva: ${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`);
      },
      (error) => {
        setSavingLocation(false);
        toast.error("Erro ao obter localização. Verifique as permissões do GPS.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleGoToMap = (localizacao) => {
    if (localizacao && localizacao.lat && localizacao.lng) {
      window.open(`https://maps.google.com/?q=${localizacao.lat},${localizacao.lng}`, '_blank');
    } else {
      toast.error("Este cliente não possui localização salva");
    }
  };

  const handleDownloadRelatorioPDF = async () => {
    try {
      toast.info("Gerando relatório PDF...");
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const response = await axios.get(`${API}/clientes/relatorio-pdf${params.toString() ? '?' + params.toString() : ''}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_clientes${searchQuery ? '_' + searchQuery.replace(/\s+/g, '_') : ''}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Relatório gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar relatório PDF");
    }
  };

  const handleToggleBlock = async (cliente) => {
    try {
      const newStatus = !cliente.bloqueado;
      await axios.put(`${API}/clientes/${cliente.id}`, {
        ...{
          nome_completo: cliente.nome_completo,
          telefone: cliente.telefone,
          cpf_rg: cliente.cpf_rg,
          carro_id: cliente.carro_id,
          rota_id: cliente.rota_id,
          endereco: cliente.endereco || "",
          bairro: cliente.bairro || "",
          endereco_secundario: cliente.endereco_secundario || "",
          cidade_estado: cliente.cidade_estado || "",
          data_nascimento: cliente.data_nascimento || "",
          localizacao: cliente.localizacao || null,
        },
        bloqueado: newStatus
      });
      toast.success(newStatus ? "Cliente bloqueado" : "Cliente desbloqueado");
      loadClientes();
    } catch (error) {
      toast.error("Erro ao alterar status do cliente");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      // Vendedor não envia campo bloqueado
      if (!isAdmin) {
        delete payload.bloqueado;
      }
      // Converter limite_compra para float ou null
      if (payload.limite_compra === '' || payload.limite_compra === undefined) {
        if (editingCliente) {
          payload.limite_compra = null;
        } else {
          delete payload.limite_compra; // Deixar o backend usar o default (500)
        }
      } else {
        payload.limite_compra = parseFloat(payload.limite_compra) || null;
      }
      
      if (editingCliente) {
        await axios.put(`${API}/clientes/${editingCliente.id}`, payload);
        toast.success("Cliente atualizado com sucesso!");
      } else {
        await axios.post(`${API}/clientes`, payload);
        toast.success("Cliente cadastrado com sucesso!");
      }
      
      setDialogOpen(false);
      setEditingCliente(null);
      resetForm();
      loadClientes();
    } catch (error) {
      toast.error(editingCliente ? "Erro ao atualizar cliente" : "Erro ao cadastrar cliente");
    }
  };

  const resetForm = () => {
    setFormData({
      nome_completo: "",
      telefone: "",
      cpf_rg: "",
      endereco: "",
      bairro: "",
      endereco_secundario: "",
      cidade_estado: "",
      data_nascimento: "",
      carro_id: "",
      rota_id: "",
      localizacao: null,
      bloqueado: false,
      limite_compra: "",
      nivel: "bronze",
      tipo_cliente: "salao_revenda"
    });
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nome_completo: cliente.nome_completo,
      telefone: cliente.telefone,
      cpf_rg: cliente.cpf_rg,
      endereco: cliente.endereco || "",
      bairro: cliente.bairro || "",
      endereco_secundario: cliente.endereco_secundario || "",
      cidade_estado: cliente.cidade_estado || "",
      data_nascimento: cliente.data_nascimento || "",
      carro_id: cliente.carro_id || "",
      rota_id: cliente.rota_id || "",
      localizacao: cliente.localizacao || null,
      bloqueado: cliente.bloqueado || false,
      limite_compra: cliente.limite_compra != null ? cliente.limite_compra.toString() : "",
      nivel: cliente.nivel || "bronze",
      tipo_cliente: cliente.tipo_cliente || "salao_revenda"
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (cliente) => {
    setClienteToDelete(cliente);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/clientes/${clienteToDelete.id}`);
      toast.success("Cliente excluído com sucesso!");
      setDeleteDialogOpen(false);
      setClienteToDelete(null);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir cliente");
    }
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingCliente(null);
      resetForm();
    }
  };

  const filteredClientes = clientes.filter(c => {
    const q = searchQuery.toLowerCase();
    const matchQuery = (
      c.nome_completo.toLowerCase().includes(q) ||
      c.cpf_rg.toLowerCase().includes(q) ||
      (c.telefone && c.telefone.includes(searchQuery)) ||
      (c.rota_nome && c.rota_nome.toLowerCase().includes(q))
    );
    const matchNivel = filtroNivel === 'todos' || (c.nivel || 'bronze') === filtroNivel;
    return matchQuery && matchNivel;
  });

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold" style={{fontFamily: 'Work Sans'}}>Clientes</h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={abrirMigrar}
              className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="btn-migrar-clientes"
            >
              <ArrowRightLeft className="h-4 w-4" /> Migrar Clientes
            </Button>
          )}
          {isAdmin && (
            <Button
              variant={showPendentes ? "default" : "outline"}
              onClick={() => setShowPendentes(v => !v)}
              className={`gap-2 relative ${showPendentes ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
              data-testid="btn-toggle-pendentes"
            >
              <Clock className="h-4 w-4" />
              Pendentes
              {pendentes.length > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center" data-testid="badge-pendentes-count">
                  {pendentes.length}
                </span>
              )}
            </Button>
          )}
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-blue-500 hover:bg-blue-600" data-testid="add-cliente-button">
              <Plus className="h-4 w-4" />
              Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCliente ? "Editar Cliente" : "Cadastrar Novo Cliente"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome_completo">Nome Completo *</Label>
                <Input
                  id="nome_completo"
                  data-testid="cliente-nome-input"
                  required
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({...formData, nome_completo: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone/WhatsApp *</Label>
                <Input
                  id="telefone"
                  data-testid="cliente-telefone-input"
                  required
                  value={formData.telefone}
                  onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpf_rg">CPF/RG *</Label>
                <Input
                  id="cpf_rg"
                  data-testid="cliente-cpf-input"
                  required
                  value={formData.cpf_rg}
                  onChange={(e) => setFormData({...formData, cpf_rg: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco">Endereco Principal</Label>
                <Input
                  id="endereco"
                  data-testid="cliente-endereco-input"
                  value={formData.endereco}
                  onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  data-testid="cliente-bairro-input"
                  value={formData.bairro}
                  onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco_secundario">Endereco Secundario (opcional)</Label>
                <Input
                  id="endereco_secundario"
                  data-testid="cliente-endereco2-input"
                  placeholder="Referencia ou endereco alternativo"
                  value={formData.endereco_secundario}
                  onChange={(e) => setFormData({...formData, endereco_secundario: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade_estado">Cidade e Estado *</Label>
                <Input
                  id="cidade_estado"
                  data-testid="cliente-cidade-input"
                  required
                  placeholder="Ex: Sao Paulo - SP"
                  value={formData.cidade_estado}
                  onChange={(e) => setFormData({...formData, cidade_estado: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                <Input
                  id="data_nascimento"
                  data-testid="cliente-data-input"
                  type="date"
                  value={formData.data_nascimento}
                  onChange={(e) => setFormData({...formData, data_nascimento: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="limite_compra">Limite de Compra (R$)</Label>
                {isAdmin ? (
                  <Input
                    id="limite_compra"
                    data-testid="cliente-limite-compra-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Padrão: R$ 500,00"
                    value={formData.limite_compra}
                    onChange={(e) => setFormData({...formData, limite_compra: e.target.value})}
                  />
                ) : (
                  <Input
                    id="limite_compra"
                    data-testid="cliente-limite-compra-input"
                    type="text"
                    readOnly
                    value={formData.limite_compra ? `R$ ${parseFloat(formData.limite_compra).toFixed(2)}` : 'R$ 500,00 (padrão)'}
                    className="bg-gray-100 cursor-not-allowed"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Tipo de Cliente *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, tipo_cliente: 'salao_revenda'})}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors ${formData.tipo_cliente === 'salao_revenda' ? 'bg-purple-600 text-white border-purple-700' : 'bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100'}`}
                    data-testid="btn-tipo-salao"
                  >
                    SALÃO / REVENDA
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, tipo_cliente: 'cliente_final'})}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors ${formData.tipo_cliente === 'cliente_final' ? 'bg-pink-600 text-white border-pink-700' : 'bg-pink-50 text-pink-800 border-pink-200 hover:bg-pink-100'}`}
                    data-testid="btn-tipo-final"
                  >
                    CLIENTE FINAL
                  </button>
                </div>
                <p className="text-[10px] text-gray-500">Define qual tabela de preços o cliente verá na Loja Virtual.</p>
              </div>

              {isAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="nivel">Nível do Cliente</Label>
                  <select
                    id="nivel"
                    data-testid="cliente-nivel-select"
                    value={formData.nivel || 'bronze'}
                    onChange={(e) => setFormData({...formData, nivel: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="bronze">🥉 Bronze</option>
                    <option value="prata">🥈 Prata</option>
                    <option value="ouro">🥇 Ouro</option>
                    <option value="platinum">⭐ Platinum</option>
                    <option value="diamante">💎 Diamante</option>
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="carro">Carro *</Label>
                <select
                  id="carro"
                  required
                  value={formData.carro_id}
                  onChange={(e) => {
                    setFormData({...formData, carro_id: e.target.value, rota_id: ""});
                    setRotas([]);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Selecione um carro</option>
                  {carros.map((carro) => (
                    <option key={carro.id} value={carro.id}>{carro.nome}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rota">Rota *</Label>
                <select
                  id="rota"
                  required
                  value={formData.rota_id}
                  onChange={(e) => setFormData({...formData, rota_id: e.target.value})}
                  disabled={!formData.carro_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                >
                  <option value="">Selecione uma rota</option>
                  {rotas.map((rota) => (
                    <option key={rota.id} value={rota.id}>
                      {rota.nome}
                    </option>
                  ))}
                </select>
                {!formData.carro_id && (
                  <p className="text-xs text-gray-500">Selecione um carro primeiro</p>
                )}
              </div>

              {/* Salvar Localização */}
              <div className="space-y-2">
                <Label>Localização GPS</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={handleSaveLocation}
                    disabled={savingLocation}
                    data-testid="save-location-button"
                  >
                    <MapPin className="h-4 w-4" />
                    {savingLocation ? "Obtendo..." : "Salvar Localização"}
                  </Button>
                  {formData.localizacao && (
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2 text-green-600 border-green-300 hover:bg-green-50"
                      onClick={() => handleGoToMap(formData.localizacao)}
                      data-testid="go-to-map-form-button"
                    >
                      <Navigation className="h-4 w-4" />
                      Ir Agora
                    </Button>
                  )}
                </div>
                {formData.localizacao && (
                  <p className="text-xs text-green-600">
                    Lat: {formData.localizacao.lat.toFixed(6)}, Lng: {formData.localizacao.lng.toFixed(6)}
                  </p>
                )}
              </div>

              {/* Bloquear Cliente - Somente Admin */}
              {isAdmin && editingCliente && (
                <div className="flex items-center justify-between p-3 border rounded-md bg-red-50 border-red-200">
                  <div className="flex items-center gap-2">
                    <Ban className="h-4 w-4 text-red-600" />
                    <Label htmlFor="bloqueado" className="text-red-700 font-medium cursor-pointer">
                      Bloquear Cliente
                    </Label>
                  </div>
                  <Switch
                    id="bloqueado"
                    checked={formData.bloqueado}
                    onCheckedChange={(checked) => setFormData({...formData, bloqueado: checked})}
                    data-testid="block-cliente-switch"
                  />
                </div>
              )}
              
              <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600" data-testid="save-cliente-button">
                {editingCliente ? "Atualizar Cliente" : "Cadastrar Cliente"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por nome, CPF/RG, telefone ou rota..."
            className="pl-10"
            data-testid="search-cliente-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button
          variant="outline"
          onClick={handleDownloadRelatorioPDF}
          className="gap-2 whitespace-nowrap"
          data-testid="relatorio-clientes-pdf-button"
        >
          <FileText className="h-4 w-4" />
          Relatório PDF
        </Button>
      </div>

      {/* Pendentes (cadastros vindos da loja virtual aguardando aprovação) */}
      {isAdmin && showPendentes && (
        <Card className="border-2 border-amber-300 bg-amber-50/40" data-testid="card-pendentes-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Clock className="h-4 w-4" />
              Cadastros pendentes da Loja Virtual
              <span className="text-xs bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">{pendentes.length}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendentes.length === 0 ? (
              <p className="text-sm text-amber-700/70 py-4 text-center" data-testid="pendentes-vazio">Nenhum cadastro pendente no momento.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendentes.map(p => (
                  <div key={p.id} className="bg-white rounded-lg border border-amber-200 p-3 text-sm space-y-1.5" data-testid={`pendente-card-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-bold text-gray-800 truncate">{p.nome_completo}</p>
                      <span className="text-[10px] bg-amber-500 text-white rounded-full px-2 py-0.5 font-bold shrink-0">PENDENTE</span>
                    </div>
                    <p className="text-xs text-gray-600 flex items-center gap-1.5"><Phone className="h-3 w-3" /> {p.telefone || "—"}</p>
                    {p.cpf_rg && <p className="text-xs text-gray-500 flex items-center gap-1.5"><IdCard className="h-3 w-3" /> {p.cpf_rg}</p>}
                    {p.cidade && <p className="text-xs text-gray-500 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {p.cidade}</p>}
                    {p.endereco && <p className="text-[11px] text-gray-500 truncate">{p.endereco}</p>}
                    {p.instagram && <p className="text-[11px] text-pink-600">@{p.instagram.replace(/^@/, "")}</p>}
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-1"
                        onClick={() => openAprovar(p)}
                        data-testid={`btn-aprovar-${p.id}`}
                      >
                        <UserCheck className="h-3.5 w-3.5" /> Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-50 gap-1"
                        onClick={() => rejeitarPendente(p)}
                        data-testid={`btn-rejeitar-${p.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filtro por Nível */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="filtro-nivel-bar">
        <span className="text-xs font-semibold text-gray-600 mr-1">Nível:</span>
        {[
          { k: 'todos', label: 'Todos', cls: 'bg-gray-100 text-gray-700 hover:bg-gray-200', active: 'bg-gray-700 text-white' },
          { k: 'bronze', label: 'Bronze', cls: 'bg-orange-50 text-orange-800 hover:bg-orange-100', active: 'bg-orange-600 text-white' },
          { k: 'prata', label: 'Prata', cls: 'bg-slate-100 text-slate-700 hover:bg-slate-200', active: 'bg-slate-500 text-white' },
          { k: 'ouro', label: 'Ouro', cls: 'bg-yellow-50 text-yellow-800 hover:bg-yellow-100', active: 'bg-yellow-500 text-white' },
          { k: 'platinum', label: 'Platinum', cls: 'bg-cyan-50 text-cyan-800 hover:bg-cyan-100', active: 'bg-cyan-600 text-white' },
          { k: 'diamante', label: 'Diamante', cls: 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100', active: 'bg-indigo-600 text-white' },
        ].map(f => {
          const count = f.k === 'todos' ? clientes.length : clientes.filter(c => (c.nivel || 'bronze') === f.k).length;
          return (
            <button
              key={f.k}
              onClick={() => setFiltroNivel(f.k)}
              data-testid={`filtro-nivel-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filtroNivel === f.k ? f.active : f.cls}`}
            >
              {f.label}
              {f.k !== 'todos' && <span className="ml-1 opacity-75">({count})</span>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClientes.map((cliente) => (
          <Card 
            key={cliente.id} 
            className={`hover:shadow-md transition-shadow ${cliente.bloqueado ? 'border-red-300 bg-red-50/50' : ''}`}
            data-testid={`cliente-card-${cliente.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-lg flex items-center gap-1.5">
                    <NivelBadge nivel={cliente.nivel} />
                    <span>{cliente.nome_completo}</span>
                  </CardTitle>
                  {((cliente.tipo_cliente || 'salao_revenda') === 'cliente_final') ? (
                    <span className="text-[10px] bg-pink-100 text-pink-800 border border-pink-300 px-2 py-0.5 rounded-full font-bold" data-testid={`tipo-badge-${cliente.id}`}>CLIENTE FINAL</span>
                  ) : (
                    <span className="text-[10px] bg-purple-100 text-purple-800 border border-purple-300 px-2 py-0.5 rounded-full font-bold" data-testid={`tipo-badge-${cliente.id}`}>SALÃO/REVENDA</span>
                  )}
                  {cliente.bloqueado && (
                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-medium" data-testid={`blocked-badge-${cliente.id}`}>
                      BLOQUEADO
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  {cliente.localizacao && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleGoToMap(cliente.localizacao)}
                      data-testid={`go-map-${cliente.id}`}
                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      title="Ir Agora (Google Maps)"
                    >
                      <Navigation className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => abrirHistorico(cliente)}
                      data-testid={`historico-cliente-${cliente.id}`}
                      className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Histórico de compras"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(cliente)}
                      data-testid={`edit-cliente-${cliente.id}`}
                      className="h-8 w-8 p-0"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(cliente)}
                      data-testid={`delete-cliente-${cliente.id}`}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="h-4 w-4" />
                {cliente.telefone}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <IdCard className="h-4 w-4" />
                {cliente.cpf_rg}
              </div>
              {cliente.cidade_estado && (
                <p className="text-gray-600 text-sm font-medium mt-1">
                  <MapPin className="h-3.5 w-3.5 inline mr-1" />
                  {cliente.cidade_estado}
                </p>
              )}
              {cliente.rota_nome && (
                <p className="text-blue-600 text-xs font-medium">
                  Rota: {cliente.rota_nome}
                </p>
              )}
              {cliente.endereco && (
                <p className="text-gray-500 text-xs">{cliente.endereco}</p>
              )}
              {cliente.limite_compra != null && cliente.limite_compra > 0 && (
                <p className="text-blue-700 text-xs font-semibold" data-testid={`limite-${cliente.id}`}>
                  Limite: R$ {cliente.limite_compra.toFixed(2)}
                </p>
              )}
              {/* Admin block toggle direto no card */}
              {isAdmin && (
                <div className="flex items-center justify-between pt-2 border-t mt-2">
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Ban className="h-3 w-3" />
                    Bloquear
                  </span>
                  <Switch
                    checked={cliente.bloqueado || false}
                    onCheckedChange={() => handleToggleBlock(cliente)}
                    data-testid={`toggle-block-${cliente.id}`}
                    className="scale-75"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dialog: Aprovar Cadastro Pendente */}
      <Dialog open={aprovarOpen} onOpenChange={(open) => { setAprovarOpen(open); if (!open) setClienteAprovando(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-aprovar-cadastro">
          <DialogHeader>
            <DialogTitle>Aprovar cadastro pendente</DialogTitle>
          </DialogHeader>
          {clienteAprovando && (
            <form onSubmit={submitAprovar} className="space-y-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs space-y-1">
                <p><strong>Nome:</strong> {clienteAprovando.nome_completo}</p>
                <p><strong>Telefone:</strong> {clienteAprovando.telefone}</p>
                {clienteAprovando.cpf_rg && <p><strong>CPF/RG:</strong> {clienteAprovando.cpf_rg}</p>}
                {clienteAprovando.endereco && <p><strong>Endereço:</strong> {clienteAprovando.endereco}</p>}
                {clienteAprovando.cidade && <p><strong>Cidade:</strong> {clienteAprovando.cidade}</p>}
              </div>

              <div className="space-y-2">
                <Label>Carro *</Label>
                <select
                  required
                  value={aprovarForm.carro_id}
                  onChange={(e) => setAprovarForm({ ...aprovarForm, carro_id: e.target.value, rota_id: "" })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="aprovar-select-carro"
                >
                  <option value="">Selecione um carro</option>
                  {carros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <Label>Rota *</Label>
                <select
                  required
                  value={aprovarForm.rota_id}
                  onChange={(e) => setAprovarForm({ ...aprovarForm, rota_id: e.target.value })}
                  disabled={!aprovarForm.carro_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                  data-testid="aprovar-select-rota"
                >
                  <option value="">Selecione uma rota</option>
                  {aprovarRotas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                </select>
                {!aprovarForm.carro_id && <p className="text-xs text-gray-500">Selecione um carro primeiro</p>}
              </div>

              <div className="space-y-2">
                <Label>Tipo de Cliente *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAprovarForm({ ...aprovarForm, tipo_cliente: "salao_revenda" })}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors ${aprovarForm.tipo_cliente === "salao_revenda" ? "bg-purple-600 text-white border-purple-700" : "bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100"}`}
                    data-testid="aprovar-btn-tipo-salao"
                  >
                    SALÃO / REVENDA
                  </button>
                  <button
                    type="button"
                    onClick={() => setAprovarForm({ ...aprovarForm, tipo_cliente: "cliente_final" })}
                    className={`rounded-lg border-2 px-3 py-2 text-sm font-bold transition-colors ${aprovarForm.tipo_cliente === "cliente_final" ? "bg-pink-600 text-white border-pink-700" : "bg-pink-50 text-pink-800 border-pink-200 hover:bg-pink-100"}`}
                    data-testid="aprovar-btn-tipo-final"
                  >
                    CLIENTE FINAL
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="aprovar-limite">Limite de Compra (R$)</Label>
                <Input
                  id="aprovar-limite"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ex: 500.00"
                  value={aprovarForm.limite_compra}
                  onChange={(e) => setAprovarForm({ ...aprovarForm, limite_compra: e.target.value })}
                  data-testid="aprovar-input-limite"
                />
                <p className="text-[11px] text-gray-500">Deixe em branco ou 0 para usar o padrão.</p>
              </div>

              <Button type="submit" disabled={aprovando} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="btn-confirmar-aprovacao">
                <UserCheck className="h-4 w-4" />
                {aprovando ? "Aprovando..." : "Aprovar cadastro"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o cliente <strong>{clienteToDelete?.nome_completo}</strong>?
              Esta ação não pode ser desfeita.
              {clienteToDelete && (
                <span className="block mt-2 text-sm text-red-600">
                  Nota: Clientes com promissórias vinculadas não podem ser excluídos.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-cliente"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {filteredClientes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">Nenhum cliente encontrado</p>
        </div>
      )}

      {/* ===== Dialog: Histórico de Compras do Cliente ===== */}
      <Dialog open={historicoOpen} onOpenChange={(o) => { setHistoricoOpen(o); if (!o) { setHistoricoData(null); setNotaVisualizando(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-historico-cliente">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-700" /> Histórico de compras
              {historicoData?.cliente_nome && <span className="text-gray-500 font-normal text-base">— {historicoData.cliente_nome}</span>}
            </DialogTitle>
          </DialogHeader>
          {historicoLoading ? (
            <p className="text-center py-8 text-gray-500">Carregando…</p>
          ) : historicoData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-bold text-blue-700">Notas</div>
                  <div className="text-xl font-extrabold">{historicoData.total_notas}</div>
                </div>
                <div className="bg-slate-50 border rounded-lg p-3">
                  <div className="text-[10px] uppercase font-bold text-gray-600">Comprado</div>
                  <div className="text-lg font-extrabold">R$ {historicoData.total_comprado.toFixed(2)}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-bold text-emerald-700">Pago</div>
                  <div className="text-lg font-extrabold">R$ {historicoData.total_pago.toFixed(2)}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase font-bold text-red-700">Devendo</div>
                  <div className="text-lg font-extrabold text-red-700">R$ {historicoData.total_devendo.toFixed(2)}</div>
                </div>
              </div>
              {historicoData.promissorias.length === 0 ? (
                <p className="text-center py-6 text-gray-500">Este cliente ainda não tem compras registradas.</p>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-xs uppercase text-gray-600">
                        <th className="py-2 px-3">Pedido</th>
                        <th className="py-2 px-3">Data</th>
                        <th className="py-2 px-3">Status</th>
                        <th className="py-2 px-3 text-right">Total</th>
                        <th className="py-2 px-3 text-right">Restante</th>
                        <th className="py-2 px-3 w-16"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historicoData.promissorias.map((p) => {
                        const statusCor = {
                          quitada: "bg-emerald-100 text-emerald-800",
                          ativa: "bg-blue-100 text-blue-800",
                          vencida: "bg-red-100 text-red-800",
                          observacao: "bg-amber-100 text-amber-800",
                          cancelada: "bg-gray-100 text-gray-600",
                        }[p.status] || "bg-gray-100 text-gray-700";
                        return (
                          <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`historico-row-${p.id}`}>
                            <td className="py-2 px-3 font-semibold">#{p.numero_pedido}</td>
                            <td className="py-2 px-3 text-gray-600">{p.data_compra?.split('-').reverse().join('/')}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${statusCor}`}>{p.status}</span>
                            </td>
                            <td className="py-2 px-3 text-right font-semibold">R$ {p.valor_total.toFixed(2)}</td>
                            <td className="py-2 px-3 text-right font-bold text-red-700">R$ {p.valor_restante.toFixed(2)}</td>
                            <td className="py-2 px-3 text-center">
                              <Button size="sm" variant="ghost" onClick={() => abrirNotaCompleta(p.id)} data-testid={`historico-ver-${p.id}`} title="Ver nota">
                                <Eye className="h-4 w-4 text-blue-700" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Dialog: Visualização de Nota (espelho) */}
      <Dialog open={!!notaVisualizando} onOpenChange={(o) => !o && setNotaVisualizando(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nota #{notaVisualizando?.numero_pedido}</DialogTitle>
          </DialogHeader>
          {notaVisualizando && (
            <div className="text-sm space-y-2 border rounded-lg p-4 font-mono">
              <div className="text-center font-bold border-b pb-2">
                NOTA PROMISSÓRIA
                <div className="text-xs text-gray-600">Pedido #{notaVisualizando.numero_pedido} · {notaVisualizando.data_compra?.split('-').reverse().join('/')}</div>
              </div>
              <div><strong>Cliente:</strong> {notaVisualizando.cliente_nome}</div>
              <div><strong>Vencimento:</strong> {notaVisualizando.data_vencimento?.split('-').reverse().join('/')}</div>
              <div className="border-t pt-2">
                <strong>Itens:</strong>
                {(notaVisualizando.itens || []).map((it, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span>{it.quantidade}x {it.nome_produto || it.produto_nome}</span>
                    <span>R$ {(it.subtotal || (it.quantidade * (it.preco_unitario || 0))).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>TOTAL</span>
                <span>R$ {notaVisualizando.valor_total?.toFixed(2)}</span>
              </div>
              {(notaVisualizando.pagamentos || []).length > 0 && (
                <div className="border-t pt-2">
                  <strong>Pagamentos:</strong>
                  {notaVisualizando.pagamentos.map((pg, idx) => (
                    <div key={idx} className={`flex justify-between text-xs ${pg.invalido ? 'line-through opacity-40' : ''}`}>
                      <span>{pg.forma_pagamento} · {(pg.data_pagamento || '').split('T')[0].split('-').reverse().join('/')}</span>
                      <span>R$ {pg.valor?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Dialog: Migrar Clientes ===== */}
      <Dialog open={migrarOpen} onOpenChange={setMigrarOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dialog-migrar-clientes">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <ArrowRightLeft className="h-5 w-5" /> Migrar Clientes em Massa
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  className="pl-10"
                  placeholder="Buscar (nome, telefone, CPF)…"
                  value={migrarBusca}
                  onChange={(e) => setMigrarBusca(e.target.value)}
                  data-testid="migrar-busca"
                />
              </div>
              <select
                value={migrarFiltroCidade}
                onChange={(e) => setMigrarFiltroCidade(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-white"
                data-testid="migrar-filtro-cidade"
              >
                <option value="">🏙️ Todas as cidades</option>
                {cidadesUnicas().map((cid) => (
                  <option key={cid} value={cid}>{cid}</option>
                ))}
              </select>
              <select
                value={migrarFiltroRota}
                onChange={(e) => setMigrarFiltroRota(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-white"
                data-testid="migrar-filtro-rota"
              >
                <option value="">📍 Todas as rotas</option>
                {rotasUsadas().map((r) => (
                  <option key={r.id} value={r.id}>{r.nome}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <div className="text-xs text-gray-600">
                <strong>{migrarSelecionados.size}</strong> selecionado(s) de{" "}
                <strong>{clientesFiltradosParaMigrar().length}</strong> visível(is)
              </div>
              <Button variant="outline" size="sm" onClick={selecionarTodosMigrar} data-testid="migrar-selecionar-todos">
                <Check className="h-4 w-4 mr-1" /> Selecionar todos os visíveis
              </Button>
            </div>
            {/* Lista de clientes */}
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {clientesFiltradosParaMigrar().length === 0 ? (
                <p className="text-center text-gray-500 py-6">Nenhum cliente encontrado.</p>
              ) : (
                clientesFiltradosParaMigrar().map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b last:border-0 cursor-pointer hover:bg-slate-50 ${migrarSelecionados.has(c.id) ? 'bg-purple-50' : ''}`}
                    data-testid={`migrar-row-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={migrarSelecionados.has(c.id)}
                      onChange={() => toggleSelMigrar(c.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{c.nome_completo}</div>
                      <div className="text-xs text-gray-500">{c.telefone} · {c.cpf_rg}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            {/* Destino */}
            <div className="border-t pt-3">
              <div className="text-sm font-bold text-purple-800 mb-2">Migrar para:</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Carro de destino</Label>
                  <select
                    value={migrarCarroDest}
                    onChange={(e) => {
                      setMigrarCarroDest(e.target.value);
                      setMigrarRotaDest("");
                      carregarRotasDest(e.target.value);
                    }}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-white"
                    data-testid="migrar-carro-select"
                  >
                    <option value="">— manter carro atual —</option>
                    {carros.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}{c.placa ? ` — ${c.placa}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Rota de destino</Label>
                  <select
                    value={migrarRotaDest}
                    onChange={(e) => setMigrarRotaDest(e.target.value)}
                    disabled={!migrarCarroDest}
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-white disabled:bg-gray-100"
                    data-testid="migrar-rota-select"
                  >
                    <option value="">— manter rota atual —</option>
                    {migrarRotasList.filter(r => r.tipo === "novas_vendas").map((r) => (
                      <option key={r.id} value={r.id}>{r.nome}</option>
                    ))}
                  </select>
                  {!migrarCarroDest && <p className="text-[11px] text-gray-500 mt-1">Escolha um carro primeiro.</p>}
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Atenção:</strong> Todas as notas dos clientes selecionados (inclusive quitadas e canceladas)
                receberão o novo carro. As rotas dos clientes serão redefinidas para a rota escolhida (substitui as anteriores).
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t mt-4">
            <Button variant="outline" onClick={() => setMigrarOpen(false)} disabled={migrando}>Cancelar</Button>
            <Button
              onClick={confirmarMigracao}
              disabled={migrando || migrarSelecionados.size === 0 || (!migrarCarroDest && !migrarRotaDest)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-confirmar-migracao"
            >
              {migrando ? "Migrando..." : <>Migrar {migrarSelecionados.size} cliente(s) <ArrowRightLeft className="h-4 w-4 ml-1" /></>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
